// Map #1 Canyon Meadow, audio half (src/audio/mapAudioDef.ts). Sunlit grassland ringed by canyon cliffs.
// Ambience: wind gusting over open grass with a leaf/grass rustle, songbirds and chirping birds by day, a far hawk and the
// odd passing bee; crickets and an owl at night. The cliffs give one-shots a soft echo.
// Music: the shared battle theme stays the default here (it is this arena's established track); the procedural score below
// (D mixolydian, 132 bpm, bright march) plays with ?music=gen.
import { MODES, type MapAudioDef } from '../../audio/mapAudioDef';

export const AUDIO: MapAudioDef = {
  id: 'canyon-meadow',
  ambience: {
    level: 0.7, // owner 2026-11-02: 'canyon breeze and ambient sound is too much' (was 0.94; gusts softened below)
    beds: [
      { src: 'pink', filter: 'lowpass', freq: 460, gain: 0.45, night: 0.8, storm: 1.5, gust: { depth: 0.3, every: [4, 9], sweep: 2.6 } },
      { src: 'white', filter: 'bandpass', freq: 2600, q: 0.6, gain: 0.06, night: 0.6, gust: { depth: 0.4, every: [3, 7], sweep: 2 } },
      { src: 'brown', filter: 'lowpass', freq: 120, gain: 0.35, night: 1.1 },
    ],
    events: [
      { kind: 'songbird', every: [5, 11], gain: 0.18, day: 1, night: 0, voices: 2, echo: 0.1 },
      { kind: 'bird', every: [3, 8], gain: 0.14, day: 1, night: 0 },
      { kind: 'raptor', every: [25, 50], gain: 0.06, day: 1, night: 0, echo: 0.35 },
      { kind: 'insect', every: [12, 25], gain: 0.06, day: 1, night: 0, pitch: 0.9 },
      { kind: 'cricket', every: [1.5, 3.5], gain: 0.05, day: 0, night: 1, voices: 3, spread: 1 },
      { kind: 'owl', every: [14, 30], gain: 0.12, day: 0, night: 1, echo: 0.35 },
      { kind: 'gust', every: [14, 28], gain: 0.12, night: 0.8, storm: 1 },
    ],
    echo: { l: 0.31, r: 0.47, fb: 0.3, damp: 2200, wet: 0.6 },
  },
  music: {
    prefer: 'sample', // owner 2026-11-02 (later): 'I missed the previous music on the first map … kind of like a rock music. Bring that back' (battle_theme.mp3)
    mood: 'bright sunlit march (D mixolydian, 132 bpm)',
    score: {
      bpm: 132, steps: 16, root: 50, mode: MODES.mixolydian, progression: [0, 6, 3, 0, 0, 6, 4, 3],
      pad: { wave: 'sawtooth', voices: 4, detune: 9, cutoff: 1800, gain: 0.06, octave: 1, send: 0.2 },
      bass: { wave: 'sawtooth', cutoff: 420, env: 2, atk: 0.005, dec: 0.15, sus: 0.5, rel: 0.08, gain: 0.3, octave: -1, legato: 0.7,
        pattern: '1.1.1.5.1.1.8.5.', hot: '11.11.5511.11.85' },
      arp: { wave: 'square', cutoff: 2400, env: 1, atk: 0.003, dec: 0.12, sus: 0.2, rel: 0.05, gain: 0.07, octave: 1, send: 0.25,
        pattern: [0, -1, 1, -1, 2, -1, 3, -1, 2, -1, 1, -1, 2, -1, 4, -1], hot: [0, 1, 2, 3, 2, 1, 2, 3, 4, 3, 2, 1, 2, 3, 5, 3] },
      lead: { wave: 'square', cutoff: 3000, q: 1, env: 0.5, atk: 0.01, dec: 0.2, sus: 0.7, rel: 0.12, gain: 0.08, octave: 1, send: 0.3,
        vibrato: { rate: 5.5, cents: 18 }, bars: [4, 5, 6, 7],
        motifs: [
          { notes: [[4, 4], [2, 2], [4, 2], [5, 4], [4, 2], [2, 2]] },
          { notes: [[0, 2], [2, 2], [4, 4], [7, 6], [null, 2]] },
          { notes: [[5, 3], [4, 3], [2, 2], [1, 4], [0, 4]] },
          { notes: [[4, 6], [5, 2], [4, 2], [2, 2], [0, 4]] },
        ] },
      drums: { kit: 'tight', gain: 0.9,
        main: { kick: 'X.....x.X.x.....', snare: '....X.......X...', hat: 'x.x.x.x.x.x.x.x.' },
        fill: { kick: 'X.....x.X.......', snare: '....X.......XxXX' },
        hot: { kick: 'X...x...X...x.x.', snare: '....X..g....X..g', hat: 'xxxxxxxxxxxxxxxx', tom: '..........x.x...' } },
      echo: { steps: 3, fb: 0.3, wet: 0.18, damp: 3000 },
      level: 0.67,
    },
  },
};
